import { useTableFilters } from "@hooks/use-table-filters";
import { IdwTable } from "../../../common/idw-table/idw-table";
import { PaginationContainer } from "../../../common/pagination-container/pagination-container";
import { resources } from "@constants/hardcoded-strings";
import { IdwTableContainer } from "../../../common/idw-table-container/idw-table-container";
import { formatDateToString, sortDates } from "@utils/date-util";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { FormattedVerificationTransactionDetailsClaim } from "@idw-types/data/verification-transaction-details";
import { sortStrings } from "@utils/string-util";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from "@constants/table-defaults";

type ClaimAssertionsTableContainerProps = {
    claims: Array<FormattedVerificationTransactionDetailsClaim>;
};

export const ClaimAssertionsTableContainer = (props: ClaimAssertionsTableContainerProps) => {
    const { claims } = props;

    const columnHelper = createColumnHelper<FormattedVerificationTransactionDetailsClaim>();
    const columns = useMemo(() => ([
        columnHelper.accessor("effectiveDate", {
            header: resources.tables.verificationTransactionDetailsClaimsAssertions.columns.date.header,
            filterFn: "dateTimeFilter",
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            sortingFn: (a, b) => sortDates(a.original.effectiveDate, b.original.effectiveDate)
        }),
        columnHelper.accessor("rpServiceName", {
            header: resources.tables.verificationTransactionDetailsClaimsAssertions.columns.verificationService.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.rpServiceName, b.original.rpServiceName)
        }),
        columnHelper.accessor("apiKey", {
            header: resources.tables.verificationTransactionDetailsClaimsAssertions.columns.apiKey.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("serviceOfferingName", {
            header: resources.tables.verificationTransactionDetailsClaimsAssertions.columns.serviceOffering.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("claimType", {
            header: resources.tables.verificationTransactionDetailsClaimsAssertions.columns.claim.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("claimStatus", {
            header: resources.tables.verificationTransactionDetailsClaimsAssertions.columns.claimStatus.header,
            filterFn: "fuzzyText"
        })
    ]), [claims]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: claims,
        filterFns: filterTypes,
        autoResetPageIndex: true,
        sortDescFirst: false,
        initialState: {
            pagination: {
                pageIndex: DEFAULT_PAGE_INDEX,
                pageSize: DEFAULT_PAGE_SIZE
            }
        },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="claim-assertions"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"claim-assertions"}
            />
            <PaginationContainer
                idPrefix="claim-assertions"
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};